import React, { useEffect, useRef, useState } from 'react';
import api from '../../utils/api';
import { tryFlushGuestSaveQueueBeforeAuth } from '../../utils/guestOfflineQueue';
import { completeAuthSession } from '../../utils/completeAuthSession';
import {
  applyOAuthPublicConfig,
  getAppleClientId,
  getGoogleClientId,
  mapAppleAuthError,
  renderGoogleButton,
  requestAppleIdToken,
} from '../../utils/socialAuth';
import styles from './SocialAuthButtons.module.css';

function GoogleMark() {
  return (
    <svg className={styles.mark} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path
        fill="#EA4335"
        d="M12 10.2v3.6h5.1c-.2 1.2-.9 2.3-1.9 3l3.1 2.4c1.8-1.7 2.9-4.1 2.9-7 0-.7-.1-1.3-.2-1.9H12z"
      />
      <path
        fill="#34A853"
        d="M6.6 14.3l-.8.6-2.3 1.8C5.1 19.3 8.3 21.2 12 21.2c2.4 0 4.4-.8 5.9-2.2l-3.1-2.4c-.8.6-1.9.9-2.8.9-2.2 0-4-1.5-4.7-3.5z"
      />
      <path
        fill="#4A90E2"
        d="M3.5 7.3C2.9 8.5 2.5 9.9 2.5 11.4c0 1.5.4 2.9 1 4.1l3.1-2.4c-.2-.6-.3-1.2-.3-1.7 0-.6.1-1.2.3-1.7L3.5 7.3z"
      />
      <path
        fill="#FBBC05"
        d="M12 5.5c1.4 0 2.6.5 3.6 1.4l2.7-2.7C16.4 2.5 14.4 1.6 12 1.6 8.3 1.6 5.1 3.5 3.5 7.3l3.1 2.4C7.9 7 9.8 5.5 12 5.5z"
      />
    </svg>
  );
}

function AppleMark() {
  return (
    <svg className={styles.mark} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path
        fill="currentColor"
        d="M17.05 12.66c-.03-2.53 2.07-3.75 2.16-3.81-1.18-1.72-3.01-1.96-3.66-1.98-1.56-.16-3.04.92-3.83.92-.79 0-2.01-.9-3.3-.87-1.7.02-3.26.99-4.13 2.51-1.76 3.05-.45 7.57 1.26 10.05.84 1.21 1.84 2.57 3.15 2.52 1.27-.05 1.74-.81 3.27-.81 1.52 0 1.95.81 3.29.78 1.36-.02 2.22-1.23 3.05-2.45.96-1.4 1.36-2.76 1.38-2.83-.03-.01-2.65-1.02-2.68-4.03zM14.7 4.45c.7-.85 1.17-2.02 1.04-3.19-1.01.04-2.23.67-2.95 1.52-.65.75-1.21 1.95-1.06 3.1 1.12.09 2.27-.57 2.97-1.43z"
      />
    </svg>
  );
}

/**
 * Google + Apple sign-in controls for Login and Register.
 * Buttons render when the server has the provider configured and a client ID is
 * available from the API or the production build env.
 */
export default function SocialAuthButtons({
  setAuth,
  navigate,
  location,
  showInfo,
  prefetchOnLogin,
  onError,
  mode = 'signin',
}) {
  const googleHitRef = useRef(null);
  const [busy, setBusy] = useState(null);
  const [enabled, setEnabled] = useState({
    google: Boolean(getGoogleClientId()),
    apple: Boolean(getAppleClientId()),
  });

  useEffect(() => {
    let cancelled = false;
    api
      .get('/api/auth/oauth/config')
      .then((res) => {
        if (cancelled) return;
        applyOAuthPublicConfig(res.data);
        setEnabled({
          google: Boolean(getGoogleClientId()) && Boolean(res.data?.google),
          apple: Boolean(getAppleClientId()) && Boolean(res.data?.apple),
        });
      })
      .catch(() => {
        /* Keep env-based visibility if config endpoint is unreachable */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!enabled.google || !googleHitRef.current) return undefined;
    let cleanup = () => {};
    let cancelled = false;

    const runGoogleAuth = async (idToken) => {
      try {
        setBusy('google');
        await tryFlushGuestSaveQueueBeforeAuth();
        const res = await api.post('/api/auth/oauth/google', {
          idToken,
          ...(mode === 'signup' ? { acceptedTerms: true } : {}),
        });
        completeAuthSession({
          authResponse: res,
          setAuth,
          navigate,
          location,
          showInfo,
          prefetchOnLogin,
        });
      } catch (err) {
        if (err.response?.data?.code === 'terms_required') {
          onError?.(
            err.response.data.error ||
              'Create an account to continue. Confirm that you are at least 16 and agree to the Terms of Service.'
          );
          navigate('/register', {
            state: { ...(location?.state || {}), termsNotice: true },
          });
          return;
        }
        const message =
          err.response?.data?.error || err.message || 'Google sign-in failed. Please try again.';
        onError?.(message);
      } finally {
        if (!cancelled) setBusy(null);
      }
    };

    renderGoogleButton(googleHitRef.current, {
      text: mode === 'signup' ? 'signup_with' : 'continue_with',
      onCredential: runGoogleAuth,
      onError: (err) => {
        onError?.(err?.message || 'Google sign-in failed. Please try again.');
      },
    }).then((dispose) => {
      if (typeof dispose === 'function') cleanup = dispose;
    });

    return () => {
      cancelled = true;
      cleanup();
    };
  }, [enabled.google, mode, setAuth, navigate, location, showInfo, prefetchOnLogin, onError]);

  const handleApple = async () => {
    try {
      setBusy('apple');
      onError?.('');
      await tryFlushGuestSaveQueueBeforeAuth();
      const { idToken, fullName } = await requestAppleIdToken();
      const res = await api.post('/api/auth/oauth/apple', {
        idToken,
        fullName,
        ...(mode === 'signup' ? { acceptedTerms: true } : {}),
      });
      completeAuthSession({
        authResponse: res,
        setAuth,
        navigate,
        location,
        showInfo,
        prefetchOnLogin,
      });
    } catch (err) {
      if (err?.code === 'cancelled' || /cancel/i.test(err?.message || '')) {
        return;
      }
      if (err.response?.data?.code === 'terms_required') {
        onError?.(
          err.response.data.error ||
            'Create an account to continue. Confirm that you are at least 16 and agree to the Terms of Service.'
        );
        navigate('/register', {
          state: { ...(location?.state || {}), termsNotice: true },
        });
        return;
      }
      const mapped = mapAppleAuthError(err) || err.message || 'Apple sign-in failed. Please try again.';
      onError?.(mapped);
    } finally {
      setBusy(null);
    }
  };

  if (!enabled.google && !enabled.apple) {
    return null;
  }

  const googleLabel =
    busy === 'google' ? 'Connecting…' : mode === 'signup' ? 'Sign up with Google' : 'Continue with Google';
  const appleLabel =
    busy === 'apple' ? 'Connecting…' : mode === 'signup' ? 'Sign up with Apple' : 'Continue with Apple';

  return (
    <div className={styles.wrap} aria-label="Social sign-in options">
      <div className={styles.divider} role="separator">
        <span>or</span>
      </div>

      <div className={styles.stack}>
        {enabled.google && (
          <div className={`${styles.googleSlot} ${busy === 'google' ? styles.busy : ''}`}>
            <div className={`${styles.socialButton} ${styles.googleButton}`} aria-hidden="true">
              <GoogleMark />
              <span>{googleLabel}</span>
            </div>
            <div className={styles.googleHitLayer} ref={googleHitRef} aria-label={googleLabel} />
          </div>
        )}

        {enabled.apple && (
          <button
            type="button"
            className={`${styles.socialButton} ${styles.appleButton}`}
            onClick={handleApple}
            disabled={Boolean(busy)}
            aria-label={appleLabel}
          >
            <AppleMark />
            <span>{appleLabel}</span>
          </button>
        )}
      </div>
    </div>
  );
}
